import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './api';
import { toast } from '../utils/toast';
import { extractApiError } from '../utils/apiError';

export interface ApiInventoryItem {
  uuid: string;
  project_uuid: string;
  name: string;
  category: string;
  category_label?: string;
  asset_tag?: string;
  condition: string;
  condition_label?: string;
  budget_allocation?: string;
  created_by?: any;
  created_at: string;
  updated_at: string;
}

export interface PaginatedInventoryResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: ApiInventoryItem[];
}

export interface GetInventoryParams {
  projectUuid: string;
  page?: number;
  page_size?: number;
}

// Bidirectional mapping for Category
export const mapUiCategoryToApi = (category: string): string => {
  switch (category) {
    case 'Audio & Sound':
    case 'Audio':
      return 'audio_sound';
    case 'Lighting':
      return 'lighting';
    case 'Video & Visuals':
      return 'video_visuals';
    case 'Staging & Structures':
      return 'staging_structures';
    case 'Power & Electrical':
      return 'power_electrical';
    case 'Furniture & Seating':
      return 'furniture_seating';
    case 'Décor & Branding':
      return 'decor_branding';
    case 'Catering & Bar':
      return 'catering_bar';
    case 'Music & DJ Equipment':
      return 'music_dj_equipment';
    case 'Production & Event Operations':
      return 'production_event_operations';
    case 'IT, Communication & Check-in':
      return 'it_communication_checkin';
    case 'Safety, Security & Logistics':
      return 'safety_security_logistics';
    case 'Miscellaneous':
      return 'miscellaneous';
    default:
      if (
        [
          'audio_sound',
          'lighting',
          'video_visuals',
          'staging_structures',
          'power_electrical',
          'furniture_seating',
          'decor_branding',
          'catering_bar',
          'music_dj_equipment',
          'production_event_operations',
          'it_communication_checkin',
          'safety_security_logistics',
          'miscellaneous',
        ].includes(category)
      ) {
        return category;
      }
      throw new Error(
        `Invalid inventory category selected: "${category}". Please select a valid category.`,
      );
  }
};

export const mapApiCategoryToUi = (category: string): string => {
  switch (category) {
    case 'audio_sound':
      return 'Audio & Sound';
    case 'lighting':
      return 'Lighting';
    case 'video_visuals':
      return 'Video & Visuals';
    case 'staging_structures':
      return 'Staging & Structures';
    case 'power_electrical':
      return 'Power & Electrical';
    case 'furniture_seating':
      return 'Furniture & Seating';
    case 'decor_branding':
      return 'Décor & Branding';
    case 'catering_bar':
      return 'Catering & Bar';
    case 'music_dj_equipment':
      return 'Music & DJ Equipment';
    case 'production_event_operations':
      return 'Production & Event Operations';
    case 'it_communication_checkin':
      return 'IT, Communication & Check-in';
    case 'safety_security_logistics':
      return 'Safety, Security & Logistics';
    case 'miscellaneous':
      return 'Miscellaneous';
    default:
      return 'Miscellaneous';
  }
};

// Bidirectional mapping for Condition
export const mapUiConditionToApi = (condition: string): string => {
  switch (condition) {
    case 'Excellent':
      return 'excellent';
    case 'Good':
      return 'good';
    case 'Fair':
      return 'fair';
    case 'Needs Repair':
      return 'needs_repair';
    default:
      return 'excellent';
  }
};

export const mapApiConditionToUi = (
  condition: string,
): 'Excellent' | 'Good' | 'Fair' | 'Needs Repair' => {
  switch (condition) {
    case 'excellent':
      return 'Excellent';
    case 'good':
      return 'Good';
    case 'fair':
      return 'Fair';
    case 'needs_repair':
      return 'Needs Repair';
    default:
      return 'Excellent';
  }
};

export const useGetInventoryItems = (
  params: GetInventoryParams,
  options: { enabled?: boolean } = {},
) => {
  return useQuery<PaginatedInventoryResponse>({
    queryKey: ['projects', params.projectUuid, 'inventory-items', params],
    enabled: options.enabled !== false && !!params.projectUuid,
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (params.page) searchParams.set('page', String(params.page));
      if (params.page_size) searchParams.set('page_size', String(params.page_size));
      const qs = searchParams.toString();
      const { data } = await apiClient.get<PaginatedInventoryResponse>(
        `/projects/${params.projectUuid}/inventory-items/${qs ? `?${qs}` : ''}`,
      );
      return data;
    },
  });
};

export interface CreateInventoryItemPayload {
  name: string;
  category: string;
  asset_tag?: string;
  condition: string;
  budget_allocation?: string;
}

export const useCreateInventoryItem = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      projectUuid,
      payload,
    }: {
      projectUuid: string;
      payload: CreateInventoryItemPayload;
    }) => {
      const { data } = await apiClient.post<ApiInventoryItem>(
        `/projects/${projectUuid}/inventory-items/`,
        payload,
      );
      return data;
    },
    onSuccess: (_data, { projectUuid }) => {
      toast.success('Inventory item added successfully');
      queryClient.invalidateQueries({ queryKey: ['projects', projectUuid, 'inventory-items'] });
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err, 'Failed to add inventory item'));
    },
  });
};

export type UpdateInventoryItemPayload = Partial<CreateInventoryItemPayload>;

export const useUpdateInventoryItem = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      projectUuid,
      itemUuid,
      payload,
    }: {
      projectUuid: string;
      itemUuid: string;
      payload: UpdateInventoryItemPayload;
    }) => {
      const { data } = await apiClient.patch<ApiInventoryItem>(
        `/projects/${projectUuid}/inventory-items/${itemUuid}/`,
        payload,
      );
      return data;
    },
    onSuccess: (_data, { projectUuid }) => {
      toast.success('Inventory item updated successfully');
      queryClient.invalidateQueries({ queryKey: ['projects', projectUuid, 'inventory-items'] });
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err, 'Failed to update inventory item'));
    },
  });
};

export const useDeleteInventoryItem = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ projectUuid, itemUuid }: { projectUuid: string; itemUuid: string }) => {
      await apiClient.delete(`/projects/${projectUuid}/inventory-items/${itemUuid}/`);
      return { projectUuid, itemUuid };
    },
    onSuccess: ({ projectUuid }) => {
      toast.success('Inventory item deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['projects', projectUuid, 'inventory-items'] });
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err, 'Failed to delete inventory item'));
    },
  });
};

export interface ProfileInventoryItem {
  uuid: string;
  name: string;
  category: string;
  category_label?: string;
  asset_tag?: string;
  condition: string;
  condition_label?: string;
  quantity: number;
  location?: string;
  budget_allocation?: string;
  created_at: string;
  updated_at: string;
}

export interface PaginatedProfileInventoryResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: ProfileInventoryItem[];
}

export interface GetProfileInventoryParams {
  page?: number;
  page_size?: number;
}

export const useGetProfileInventoryItems = (
  params: GetProfileInventoryParams = {},
  options: { enabled?: boolean } = {},
) => {
  return useQuery<PaginatedProfileInventoryResponse>({
    queryKey: ['profile-inventory-items', params],
    enabled: options.enabled !== false,
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (params.page) searchParams.set('page', String(params.page));
      if (params.page_size) searchParams.set('page_size', String(params.page_size));
      const qs = searchParams.toString();
      const { data } = await apiClient.get<PaginatedProfileInventoryResponse>(
        `/profiles/inventory-items/${qs ? `?${qs}` : ''}`,
      );
      return data;
    },
  });
};

export interface CreateProfileInventoryItemPayload {
  name: string;
  category: string;
  asset_tag?: string;
  condition: string;
  quantity: number;
  location?: string;
  budget_allocation?: string;
}

export const useCreateProfileInventoryItem = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateProfileInventoryItemPayload) => {
      const { data } = await apiClient.post<ProfileInventoryItem>(
        `/profiles/inventory-items/`,
        payload,
      );
      return data;
    },
    onSuccess: () => {
      toast.success('Inventory item added successfully');
      queryClient.invalidateQueries({ queryKey: ['profile-inventory-items'] });
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err, 'Failed to add inventory item'));
    },
  });
};

export type UpdateProfileInventoryItemPayload = Partial<CreateProfileInventoryItemPayload>;

export const useUpdateProfileInventoryItem = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      itemUuid,
      payload,
    }: {
      itemUuid: string;
      payload: UpdateProfileInventoryItemPayload;
    }) => {
      const { data } = await apiClient.patch<ProfileInventoryItem>(
        `/profiles/inventory-items/${itemUuid}/`,
        payload,
      );
      return data;
    },
    onSuccess: () => {
      toast.success('Inventory item updated successfully');
      queryClient.invalidateQueries({ queryKey: ['profile-inventory-items'] });
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err, 'Failed to update inventory item'));
    },
  });
};

export const useDeleteProfileInventoryItem = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (itemUuid: string) => {
      await apiClient.delete(`/profiles/inventory-items/${itemUuid}/`);
      return itemUuid;
    },
    onSuccess: () => {
      toast.success('Inventory item deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['profile-inventory-items'] });
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err, 'Failed to delete inventory item'));
    },
  });
};

export interface ApiInventoryImportCandidate {
  uuid: string;
  name: string;
  category: string;
  category_label?: string;
  asset_tag?: string;
  condition: string;
  condition_label?: string;
  quantity: number;
  location?: string;
  budget_allocation?: string;
  already_imported: boolean;
  selectable: boolean;
  created_at: string;
  updated_at: string;
}

export interface PaginatedImportCandidatesResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: ApiInventoryImportCandidate[];
}

export const useGetInventoryImportCandidates = (
  projectUuid: string,
  params: { page?: number; page_size?: number } = {},
  options: { enabled?: boolean } = {},
) => {
  return useQuery<PaginatedImportCandidatesResponse>({
    queryKey: ['projects', projectUuid, 'inventory-import-candidates', params],
    enabled: options.enabled !== false && !!projectUuid,
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (params.page) searchParams.set('page', String(params.page));
      if (params.page_size) searchParams.set('page_size', String(params.page_size));
      const qs = searchParams.toString();
      const { data } = await apiClient.get<PaginatedImportCandidatesResponse>(
        `/projects/${projectUuid}/inventory-import-candidates/${qs ? `?${qs}` : ''}`,
      );
      return data;
    },
  });
};

export const useImportInventoryItems = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      projectUuid,
      sourceItemUuuids,
    }: {
      projectUuid: string;
      sourceItemUuuids: string[];
    }) => {
      const { data } = await apiClient.post(`/projects/${projectUuid}/inventory-imports/`, {
        source_item_uuids: sourceItemUuuids,
      });
      return data;
    },
    onSuccess: (_data, { projectUuid }) => {
      toast.success('Inventory items imported successfully');
      queryClient.invalidateQueries({ queryKey: ['projects', projectUuid, 'inventory-items'] });
      queryClient.invalidateQueries({
        queryKey: ['projects', projectUuid, 'inventory-import-candidates'],
      });
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err, 'Failed to import inventory items'));
    },
  });
};
